import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { THEME_COLORS } from "../../ThemeConfig";
import { AuthModalContext } from "../../contexts/AuthModalContext";
import { PatientConfig } from "../../axiosConfig";
import { Context } from "../../contexts/Store";
import LandingFooter from "./LandingFooter";
import Lottie from "lottie-react";
import LoaderLottie from "../../assets/lottie/LoaderLottie.json";
import iconTherapist from "../../assets/icons/dr-profile.svg";
import iconMatch from "../../assets/icons/find-expert-icon.svg";
import iconProgress from "../../assets/icons/session-completed-icon.svg";
import iconPricing from "../../assets/icons/payment-success-icon.svg";
import iconLanguage from "../../assets/icons/person.svg";
import iconOffers from "../../assets/images/ticket-green.svg";
import iconSignup from "../../assets/icons/person.svg";
import iconSchedule from "../../assets/icons/session-calendar-icon.svg";
import iconMatchStep from "../../assets/icons/find-expert-icon.svg";
import iconConnect from "../../assets/icons/session-call-icon.svg";
import iconConfidential from "../../assets/icons/success-tick.svg";
import iconCertified from "../../assets/icons/dr-profile.svg";
import iconSecure from "../../assets/icons/payment-success-icon.svg";

const HERO_POINTS = [
  { id: 1, label: "100% Confidential", icon: iconConfidential },
  { id: 2, label: "Certified Experts", icon: iconCertified },
  { id: 3, label: "Secure Platform", icon: iconSecure },
];

const CAROUSEL_ITEMS = [
  { id: 6, title: "Live Offers", description: "10% off on single sessions and 15% off on packages. Book your first session today!", icon: iconConfidential },
  { id: 1, title: "Experienced Therapists", description: "Experienced therapists with a minimum of 5 years of experience.", icon: iconTherapist },
  { id: 2, title: "Smart Match", description: "Get paired with the perfect therapist, tailored to your needs and preferences.", icon: iconMatch },
  { id: 3, title: "Progress Tracker", description: "Receive a customized report after every session, tracking your improvement and insights.", icon: iconProgress },
  { id: 4, title: "Transparent Pricing", description: "Enjoy standardized and affordable pricing starting from Rs. 1000, with no hidden costs.", icon: iconPricing },
  { id: 5, title: "Multilingual Sessions", description: "Get peace of mind in a comfortable language (currently Malayalam, Tamil and English are live).", icon: iconLanguage },
];

const HOW_IT_WORKS = [
  { id: 1, title: "Sign Up", desc: "Quick registration.", icon: iconSignup },
  { id: 2, title: "Schedule", desc: "Pick your time.", icon: iconSchedule },
  { id: 3, title: "Match", desc: "Right paired expert.", icon: iconMatchStep },
  { id: 4, title: "Connect", desc: "Video sessions.", icon: iconConnect },
];

export default function LandingPage() {
  const { state, dispatch } = useContext(Context);
  const { openLoginModal } = useContext(AuthModalContext);
  const auth = state.user_details?.is_verified;
  const navigate = useNavigate();

  const [concerns, setConcerns] = useState([]);
  const [loadingConcerns, setLoadingConcerns] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    const headers = {};
    if (state.user_details?.access_token) {
      headers.Authorization = `Bearer ${state.user_details.access_token}`;
    }
    PatientConfig.get("api/v1/experts/area-of-expertises/?is_common_concern=true", { headers })
      .then((res) => {
        const data = (res.data?.data || []).filter((item) => item.name !== "Other");
        setConcerns(data);
      })
      .catch(() => setConcerns([]))
      .finally(() => setLoadingConcerns(false));
  }, [state.user_details?.access_token]);

  useEffect(() => {
    const t = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % CAROUSEL_ITEMS.length);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const handleStartConsultation = () => {
    if (auth) {
      navigate("/create-session");
    } else {
      navigate("/?auth=login&next=/create-session");
    }
  };

  return (
    <>
      <Main>
        {/* Hero */}
        <HeroSection>
          <HeroInner>
          <HeroTag>YOUR COMPANION TO WELLNESS</HeroTag>
          <HeroTitle>
            Find Your Peace, <HeroTitleAccent>Anytime, Anywhere</HeroTitleAccent>
          </HeroTitle>
          <HeroDesc>
            Access expert-led therapy from the comfort of your home. 100% confidential, secure, and personalized support for your mental well-being journey.
          </HeroDesc>
          <CarouselWrapper>
            <CarouselTrack style={{ transform: `translateX(-${carouselIndex * 100}%)` }}>
              {CAROUSEL_ITEMS.map((item) => (
                <CarouselSlide key={item.id}>
                  <CarouselIconWrap>
                    <CarouselIcon src={item.icon} alt="" />
                  </CarouselIconWrap>
                  <CarouselTitle>{item.title}</CarouselTitle>
                  <CarouselDesc>{item.description}</CarouselDesc>
                </CarouselSlide>
              ))}
            </CarouselTrack>
            <CarouselDots>
              {CAROUSEL_ITEMS.map((_, i) => (
                <Dot
                  key={i}
                  active={i === carouselIndex}
                  onClick={() => setCarouselIndex(i)}
                />
              ))}
            </CarouselDots>
          </CarouselWrapper>
          <CtaButton onClick={handleStartConsultation}>
            Start Your Consultation
          </CtaButton>
          <HeroPoints>
            {HERO_POINTS.map((point) => (
              <HeroPoint key={point.id}>
                <HeroPointIcon src={point.icon} alt="" />
                <HeroPointLabel>{point.label}</HeroPointLabel>
              </HeroPoint>
            ))}
          </HeroPoints>
          </HeroInner>
        </HeroSection>

        {/* Our Services */}
        <SectionWrap alt>
        <Section>
          <SectionTag>Our Services</SectionTag>
          <SectionSubtag>Comprehensive Care for Your Mind</SectionSubtag>
          <SectionDesc>
            We offer a wide range of mental health services to support you on your wellness journey.
          </SectionDesc>
          {loadingConcerns ? (
            <LoaderWrap>
              <Lottie animationData={LoaderLottie} style={{ width: 80, height: 80 }} />
            </LoaderWrap>
          ) : (
            <>
              <CardsGrid>
                {concerns.slice(0, 4).map((item) => (
                  <ConcernCard key={item.id} to={`/concerns/${item.id}`}>
                    <ConcernCardAccent />
                    <ConcernCardBody>
                      <ConcernCardTitle>{item.name}</ConcernCardTitle>
                      <ConcernCardSubtitle>{item.title || ""}</ConcernCardSubtitle>
                      <ConcernCardDesc>{item.description || ""}</ConcernCardDesc>
                      <ConcernCardActions>
                        <ConcernLink as="span">Learn more →</ConcernLink>
                        <ConcernBookBtn
                          onClick={(e) => {
                            e.preventDefault();
                            if (auth) {
                              dispatch({ type: "ADD_SESSION", new_session: { concern: item.id } });
                              navigate("/create-session");
                            } else {
                              openLoginModal?.();
                            }
                          }}
                        >
                          Book now
                        </ConcernBookBtn>
                      </ConcernCardActions>
                    </ConcernCardBody>
                  </ConcernCard>
                ))}
              </CardsGrid>
              <ViewAllWrap>
                <ViewAllLink to="/concerns">View all options</ViewAllLink>
              </ViewAllWrap>
            </>
          )}
          <SectionCta>
            <CtaButton onClick={handleStartConsultation}>
              Start Your Consultation
            </CtaButton>
          </SectionCta>
        </Section>
        </SectionWrap>

        {/* How It Works */}
        <HowSection>
          <HowSectionInner>
          <SectionTag>How It Works</SectionTag>
          <SectionSubtag>Your Journey to Wellness</SectionSubtag>
          <SectionDesc>
            Getting started is simple. Follow these easy steps to begin your mental health journey.
          </SectionDesc>
          <StepsRow>
            {HOW_IT_WORKS.map((step) => (
              <StepCard key={step.id}>
                <StepIconWrap>
                  <StepIcon src={step.icon} alt="" />
                </StepIconWrap>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.desc}</StepDesc>
              </StepCard>
            ))}
          </StepsRow>
          </HowSectionInner>
        </HowSection>
      </Main>
      <LandingFooter />
    </>
  );
}

const Main = styled.main`
  min-height: calc(100vh - 70px);
  background: ${THEME_COLORS.page_bg};
`;
const HeroSection = styled.section`
  min-height: calc(100vh - 70px);
  padding: 48px 24px 56px;
  text-align: center;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url("${process.env.PUBLIC_URL || ""}/hero-bg.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-bottom: 1px solid rgba(0, 167, 157, 0.08);
  @media all and (max-width: 768px) {
    padding: 36px 16px 44px;
    min-height: calc(100vh - 70px);
  }
`;
const HeroInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  @media all and (max-width: 768px) {
    max-width: 100%;
  }
`;
const HeroTag = styled.p`
  display: inline-block;
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.primary};
  margin: 0 0 14px 0;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid ${THEME_COLORS.chips_green_container};
  background: ${THEME_COLORS.chips_green_container};
  @media all and (max-width: 768px) {
    font-size: 11px;
    padding: 6px 14px;
  }
`;
const HeroTitle = styled.h1`
  font-family: "DM_sans_medium";
  font-size: 36px;
  line-height: 1.25;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 12px 0;
  @media all and (max-width: 768px) {
    font-size: 26px;
    margin-bottom: 10px;
  }
`;
const HeroTitleAccent = styled.span`
  color: ${THEME_COLORS.primary};
`;
const HeroDesc = styled.p`
  font-family: "DM_sans";
  font-size: 18px;
  line-height: 1.55;
  color: ${THEME_COLORS.text_para};
  margin: 0 0 24px 0;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  @media all and (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;
const HeroPoints = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px 32px;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid ${THEME_COLORS.accent_teal_soft};
  @media all and (max-width: 768px) {
    gap: 16px 24px;
    margin-top: 22px;
    padding-top: 20px;
  }
`;
const HeroPoint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const HeroPointIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
`;
const HeroPointLabel = styled.span`
  font-family: "DM_sans_medium";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  @media all and (max-width: 480px) {
    font-size: 14px;
  }
`;
const CarouselWrapper = styled.div`
  overflow: hidden;
  border-radius: 16px;
  background: ${THEME_COLORS.card_bg};
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  padding: 24px 20px;
  margin-bottom: 24px;
  min-height: 100px;
  border: 1px solid ${THEME_COLORS.light_300};
  width: 100%;
  box-sizing: border-box;
  @media all and (max-width: 768px) {
    padding: 20px 16px;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 20px;
    border-radius: 14px;
  }
  @media all and (max-width: 480px) {
    padding: 18px 14px;
    margin-bottom: 18px;
  }
`;
const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  margin-bottom: 20px;
  width: 100%;
  @media all and (max-width: 768px) {
    margin-bottom: 16px;
  }
`;
const CarouselSlide = styled.div`
  min-width: 100%;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 4px;
  box-sizing: border-box;
  @media all and (max-width: 768px) {
    padding: 0 2px;
  }
`;
const CarouselIconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME_COLORS.primary};
  border-radius: 50%;
  flex-shrink: 0;
  @media all and (max-width: 480px) {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }
`;
const CarouselIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  @media all and (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;
const CarouselTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 22px;
  color: ${THEME_COLORS.text_title};
  margin: 0 auto 8px;
  line-height: 1.3;
  max-width: 100%;
  @media all and (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 6px;
  }
  @media all and (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 6px;
    padding: 0 4px;
  }
`;
const CarouselDesc = styled.p`
  font-family: "DM_sans";
  font-size: 16px;
  color: ${THEME_COLORS.text_para};
  line-height: 1.5;
  margin: 0 auto;
  max-width: 100%;
  @media all and (max-width: 768px) {
    font-size: 15px;
    padding: 0 4px;
  }
  @media all and (max-width: 480px) {
    font-size: 14px;
    line-height: 1.45;
    padding: 0 2px;
  }
`;
const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${(p) => (p.active ? THEME_COLORS.primary : THEME_COLORS.light_400)};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  @media all and (max-width: 480px) {
    width: 8px;
    height: 8px;
  }
`;
const CtaButton = styled.button`
  padding: 14px 32px;
  background: ${THEME_COLORS.primary};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 16px;
  border: none;
  border-radius: 32px;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0, 167, 157, 0.3);
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    opacity: 0.95;
    box-shadow: 0 4px 20px rgba(0, 167, 157, 0.35);
  }
`;
const SectionWrap = styled.div`
  width: 100%;
  background: ${(p) => (p.alt ? THEME_COLORS.section_teal_bg : "transparent")};
  padding: 32px 0 48px;
  @media all and (max-width: 768px) {
    padding: 24px 0 40px;
  }
`;
const Section = styled.section`
  padding: 40px 24px 32px;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
  @media all and (max-width: 768px) {
    padding: 32px 16px 24px;
  }
`;
const SectionTag = styled.h2`
  font-family: "DM_sans_medium";
  font-size: 24px;
  color: ${THEME_COLORS.text_title};
  text-align: center;
  margin: 0 0 8px 0;
  @media all and (max-width: 480px) {
    font-size: 22px;
  }
`;
const SectionSubtag = styled.p`
  font-family: "DM_sans_medium";
  font-size: 18px;
  color: ${THEME_COLORS.primary};
  text-align: center;
  margin: 0 0 8px 0;
  @media all and (max-width: 768px) {
    font-size: 16px;
  }
  @media all and (max-width: 480px) {
    font-size: 15px;
  }
`;
const SectionDesc = styled.p`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_para};
  text-align: center;
  max-width: 560px;
  margin: 0 auto 24px;
  line-height: 1.5;
  @media all and (max-width: 480px) {
    font-size: 14px;
    margin-left: 8px;
    margin-right: 8px;
  }
`;
const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 20px;
  @media all and (max-width: 768px) {
    gap: 16px;
    margin-bottom: 16px;
  }
`;
const ViewAllWrap = styled.div`
  text-align: center;
  margin-bottom: 24px;
  @media all and (max-width: 768px) {
    margin-bottom: 20px;
  }
`;
const ViewAllLink = styled(Link)`
  display: inline-block;
  font-family: "DM_sans_medium";
  font-size: 15px;
  color: ${THEME_COLORS.primary};
  text-decoration: none;
  padding: 10px 24px;
  border: 1px solid ${THEME_COLORS.primary};
  border-radius: 24px;
  transition: background 0.2s ease, color 0.2s ease;
  &:hover {
    background: ${THEME_COLORS.primary};
    color: ${THEME_COLORS.white};
  }
`;
const ConcernCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: ${THEME_COLORS.white};
  border-radius: 16px;
  padding: 0;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${THEME_COLORS.light_300};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s ease;
  overflow: hidden;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
    border-color: ${THEME_COLORS.chips_green_container};
  }
`;
const ConcernCardAccent = styled.div`
  height: 4px;
  background: ${THEME_COLORS.chips_green_on_container};
`;
const ConcernCardBody = styled.div`
  padding: 24px 24px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  @media all and (max-width: 480px) {
    padding: 20px 18px 18px;
  }
`;
const ConcernCardTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 20px;
  font-weight: 600;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 8px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
  @media all and (max-width: 480px) {
    font-size: 18px;
  }
`;
const ConcernCardSubtitle = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  margin: 0 0 12px 0;
  line-height: 1.4;
`;
const ConcernCardDesc = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_para};
  line-height: 1.55;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  min-height: 3.9em;
`;
const ConcernCardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid ${THEME_COLORS.divider};
`;
const ConcernLink = styled.span`
  font-family: "DM_sans_medium";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
`;
const ConcernBookBtn = styled.button`
  padding: 10px 22px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 14px;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;
  box-shadow: 0 2px 8px rgba(29, 166, 135, 0.25);
  &:hover {
    opacity: 0.95;
    transform: scale(1.02);
  }
`;
const SectionCta = styled.div`
  text-align: center;
`;
const HowSection = styled.section`
  padding: 40px 24px 48px;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  background: ${THEME_COLORS.section_alt_bg};
  @media all and (max-width: 768px) {
    padding: 32px 16px 40px;
  }
`;
const HowSectionInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;
const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 20px;
  @media all and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media all and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const StepCard = styled.div`
  background: ${THEME_COLORS.white};
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: ${THEME_COLORS.elevation_user_u3};
`;
const StepIconWrap = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME_COLORS.chips_green_container};
  border-radius: 50%;
`;
const StepIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;
const StepTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 18px;
  color: ${THEME_COLORS.text_title};
  margin-bottom: 8px;
`;
const StepDesc = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_para};
`;
